import type { ReactNode } from 'react'
import { Icone, type NomIcone } from './Icone'

/**
 * Bloc, état vide, squelette, onglets.
 *
 * Les trois derniers sont les composants qu'on ajoute « après », et qu'on n'ajoute donc jamais :
 * un écran écrit sans état vide s'expédie avec un écran blanc, et un écran écrit sans squelette
 * clignote. Ils existent ici pour que le coût d'y penser soit nul au moment d'écrire l'écran.
 */

export function Bloc({
  children,
  titre,
  actions,
  variante,
}: {
  children: ReactNode
  titre?: ReactNode
  actions?: ReactNode
  variante?: 'sourd' | 'accent' | 'serre' | 'danger' | 'nu'
}) {
  return (
    <section className={variante === undefined ? 'bloc' : `bloc bloc--${variante}`}>
      {(titre !== undefined || actions !== undefined) && (
        <div className="bloc__entete">
          {typeof titre === 'string' ? <h2>{titre}</h2> : titre}
          {actions}
        </div>
      )}
      {children}
    </section>
  )
}

/**
 * État vide.
 *
 * Jamais une phrase seule : un titre qui nomme ce qui manque, une explication, et **une action**.
 * « Aucune conversation pour le moment. » laisse l'utilisateur devant un mur ; « Personne ne vous
 * a encore écrit — parcourez les annonces » lui donne la sortie.
 */
export function EtatVide({
  icone,
  titre,
  children,
  action,
}: {
  icone: NomIcone
  titre: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="etat-vide">
      <span className="etat-vide__icone">
        <Icone nom={icone} taille="lg" />
      </span>
      <h3>{titre}</h3>
      {children !== undefined && <p>{children}</p>}
      {action}
    </div>
  )
}

/**
 * Squelette de chargement.
 *
 * Des squelettes, jamais un sablier ni « Chargement… ». Le squelette dit la FORME de ce qui
 * arrive : la page ne se réorganise pas sous les yeux quand la réponse tombe, ce qui est la
 * moitié du décalage cumulé de mise en page.
 */
export function Squelette({
  forme = 'texte',
  largeur,
  nombre = 1,
}: {
  forme?: 'texte' | 'titre' | 'image'
  largeur?: string
  nombre?: number
}) {
  return (
    <>
      {Array.from({ length: nombre }, (_, rang) => (
        <span
          key={rang}
          className={`squelette squelette--${forme}`}
          style={largeur === undefined ? undefined : { display: 'block', inlineSize: largeur }}
          aria-hidden="true"
        />
      ))}
    </>
  )
}

/**
 * Le squelette d'une LISTE — ticket `fo-annonce-02`.
 *
 * **Deux formes suffisent pour trente écrans, et c'est délibéré.** Un squelette taillé au
 * millimètre pour chaque écran serait un second écran à maintenir, qui se désaccorderait du
 * premier à la première modification — et un squelette faux est pire qu'un squelette
 * approximatif : il promet une mise en page qui ne viendra pas. Ce qu'il doit dire est le RYTHME
 * de ce qui arrive : combien de blocs, de quelle hauteur.
 *
 * `aria-busy` sur le conteneur et `aria-hidden` sur les barres : un lecteur d'écran annonce
 * « occupé » une fois, il ne lit pas quinze rectangles vides.
 */
export function SqueletteListe({ lignes = 3 }: { lignes?: number }) {
  return (
    <div className="squelette-pile" aria-busy="true">
      {Array.from({ length: lignes }, (_, rang) => (
        <div key={rang} className="bloc squelette-bloc">
          <Squelette forme="titre" />
          <Squelette forme="texte" largeur="90%" />
          <Squelette forme="texte" largeur="60%" />
        </div>
      ))}
    </div>
  )
}

/**
 * Le squelette d'une PAGE de détail — en-tête, puis des sections.
 *
 * Même raison d'être que `SqueletteListe`, autre rythme : un titre large et court, puis des
 * paragraphes. C'est la forme de tout ce qui n'est pas une liste — un dossier, un bail, un profil,
 * un questionnaire.
 */
export function SquelettePage({ sections = 2 }: { sections?: number }) {
  return (
    <div className="squelette-pile" aria-busy="true">
      <div className="squelette-entete">
        <Squelette forme="titre" largeur="45%" />
        <Squelette forme="texte" largeur="70%" />
      </div>

      {Array.from({ length: sections }, (_, rang) => (
        <div key={rang} className="bloc squelette-bloc">
          <Squelette forme="titre" largeur="35%" />
          <Squelette forme="texte" />
          <Squelette forme="texte" largeur="85%" />
          <Squelette forme="texte" largeur="55%" />
        </div>
      ))}
    </div>
  )
}

/**
 * Barre d'onglets interne à un écran.
 *
 * `aria-selected` porte l'état, et le CSS s'y accroche : les deux rendus — visuel et lecteur
 * d'écran — ne peuvent pas se désynchroniser, puisqu'il n'y a qu'une source.
 */
export function Onglets<T extends string>({
  onglets,
  actif,
  onChange,
  ariaLabel,
}: {
  onglets: readonly { cle: T; libelle: ReactNode }[]
  actif: T
  onChange: (cle: T) => void
  ariaLabel: string
}) {
  return (
    <div className="onglets" role="tablist" aria-label={ariaLabel}>
      {onglets.map((onglet) => (
        <button
          key={onglet.cle}
          type="button"
          role="tab"
          aria-selected={onglet.cle === actif}
          onClick={() => onChange(onglet.cle)}
        >
          {onglet.libelle}
        </button>
      ))}
    </div>
  )
}
